import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { KeyRound } from "lucide-react";

/**
 * Phase 22 — Set-password page. Landing target of the invite email +
 * platform_admin grant flow. Consumes ``POST /api/auth/set-password``.
 */
export default function SetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    if (password !== confirm) {
      toast.error("Passwords do not match");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/set-password", {
        token, password, confirm_password: confirm,
      });
      setDone(true);
      toast.success("Password set — sign in below.");
      setTimeout(() => navigate("/login", { replace: true }), 1200);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  if (!token) {
    return (
      <div className="min-h-screen grid place-items-center bg-background" data-testid="set-password-page">
        <Card className="max-w-md">
          <CardContent className="p-6 text-sm text-muted-foreground">
            Missing token — check the link in your invite email.
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen grid place-items-center bg-background px-6" data-testid="set-password-page">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 font-display text-2xl text-primary">
            <KeyRound className="h-5 w-5 text-secondary" /> Set your password
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-3">
            <div>
              <Label htmlFor="pw">New password</Label>
              <Input
                id="pw" type="password"
                value={password} onChange={(e) => setPassword(e.target.value)}
                minLength={8} autoComplete="new-password"
                data-testid="set-password-input"
              />
            </div>
            <div>
              <Label htmlFor="cf">Confirm password</Label>
              <Input
                id="cf" type="password"
                value={confirm} onChange={(e) => setConfirm(e.target.value)}
                minLength={8} autoComplete="new-password"
                data-testid="set-password-confirm-input"
              />
            </div>
            <Button
              type="submit" disabled={busy || done}
              className="w-full"
              data-testid="set-password-submit-btn"
            >
              {busy ? "Setting…" : done ? "Redirecting…" : "Set password"}
            </Button>
            <div className="text-center">
              <Link to="/login" className="text-xs text-muted-foreground hover:text-primary">
                Back to sign in
              </Link>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
